import { useState } from 'react';
import { FileText, ChevronRight, ChevronDown, ExternalLink, Search } from 'lucide-react';

// The Knowledge Base lives on the DoyleIntranet SharePoint site (same base the
// admin surfaces already point at). When a citation has no direct item URL, we
// fall back to a KB search for the document title so the tech lands somewhere
// useful instead of dead text.
const KB_SITE_URL = 'https://godoyle.sharepoint.com/sites/DoyleIntranet';

/** SharePoint modern-search URL for the KB site, scoped to a query string. */
export function kbSearchUrl(query: string): string {
  return `${KB_SITE_URL}/_layouts/15/search.aspx/?q=${encodeURIComponent(query.trim())}`;
}

/**
 * Turn a raw citation title / filename into something readable for display:
 * drop a trailing file extension, replace underscores with spaces, and title-case
 * — while preserving model numbers (XR550, B9512G) and short acronyms (DMP, DSC,
 * NFPA). The raw value is kept verbatim as a tooltip, so nothing is lost.
 */
export function humanizeTitle(raw: string): string {
  const base = (raw ?? '').trim();
  if (!base) return '';
  const noExt = base.replace(/\.[A-Za-z0-9]{1,5}$/, '');
  const spaced = noExt.replace(/_+/g, ' ').replace(/\s+/g, ' ').trim();
  const source = spaced || noExt.trim() || base;
  const humanized = source
    .split(' ')
    .map((word) => {
      if (!word) return word;
      // Model numbers / mixed alphanumerics stay as authored.
      if (/\d/.test(word)) return word;
      // Short all-caps acronyms stay uppercase.
      if (word.length <= 4 && word === word.toUpperCase()) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
  return humanized || base;
}

// D3 — one collapsed line of provenance under a grounded answer. The forensic
// detail (retrieval rungs, chunk-level scores) lives in F19, not here.
//
// Field presence is the signal (matching the M21 convention): v2-grounded turns
// carry a non-empty `sources` array; v1/vision turns and refusals carry none, so
// this renders nothing (no empty shell).

export interface SourceInfo {
  title: string;
  source: string;
  relevance: number;
  metadata_spo_item_weburi?: string | null;
}

const MAX_COLLAPSED = 3;

/**
 * Collapse chunk-level sources to one row per document. Multiple retrieved
 * chunks from the same file share a title/source, so key on source path (then
 * title) and keep first occurrence — the backend orders by relevance, so the
 * strongest hit for each document wins.
 */
export function dedupeSources(sources: SourceInfo[]): SourceInfo[] {
  const seen = new Set<string>();
  const out: SourceInfo[] = [];
  for (const s of sources) {
    const key = (s.source || s.title || '').trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

export function SourceLine({ sources }: { sources?: SourceInfo[] }) {
  const [expanded, setExpanded] = useState(false);
  const docs = dedupeSources(sources ?? []);
  if (docs.length === 0) return null;

  const shown = docs.slice(0, MAX_COLLAPSED);
  const extra = docs.length - shown.length;

  return (
    <div className="source-line">
      <button
        type="button"
        className="source-line-toggle"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown size={13} aria-hidden="true" /> : <ChevronRight size={13} aria-hidden="true" />}
        <FileText size={13} aria-hidden="true" />
        <span className="source-line-label">Sources:</span>
        {!expanded && (
          <span className="source-line-summary" title={shown.map((s) => s.title).join(', ')}>
            {shown.map((s) => humanizeTitle(s.title)).join(', ')}
            {extra > 0 ? ` +${extra} more` : ''}
          </span>
        )}
      </button>

      {expanded && (
        <ul className="source-line-list">
          {docs.map((s, i) => {
            const display = humanizeTitle(s.title);
            const direct = s.metadata_spo_item_weburi;
            // No direct item URL → land the user on a KB search for the title
            // rather than dead text.
            const href = direct || kbSearchUrl(display || s.title);
            return (
              <li key={i}>
                <a
                  className="source-line-link"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={s.title}
                >
                  {display}
                  {direct ? (
                    <ExternalLink size={11} aria-hidden="true" />
                  ) : (
                    <Search size={11} aria-hidden="true" />
                  )}
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
